import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTriangleExclamation,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";
import { fmtMoney, fmtLbs, fmtPct } from "../../utils/inventory/receiverMfcaUtils";
import "../../style/mfca-dashboard.css";

// 3-tier color band — matches the rejection rate tier used previously. low
// rejection is healthy, not red. note this drives the rate value's text
// color, not the section icon — the icon is a static blue under the page's
// unified visual language.
const rejectionPctClass = (pct) => {
  if (pct <= 5) return "is-good";
  if (pct <= 15) return "is-warn";
  return "is-rejected";
};

// compact rejection-rate + dispositions summary. lifted from the old
// ReceiverRejectionBreakdown's right-column block, now a standalone card so it
// can share a row with the Material Mix card under the KPIs.
const RejectionSummaryCard = ({ counters, rejectionRateDelta }) => {
  const { t } = useTranslation();

  const totalDelivered = counters?.totalDeliveredLbs || 0;
  const totalRejected = counters?.totalRejectedLbs || 0;
  const rejectionRate = counters?.rejectionRate || 0;
  const disposalLoss = counters?.notUtilized?.disposalLoss || 0;
  const qualityIssue = counters?.notUtilized?.qualityIssue || 0;

  return (
    <div className="mfca-card rl-rejection-summary-card">
      <div className="mfca-card-header">
        <div className="mfca-card-icon mfca-icon-blue">
          <FontAwesomeIcon icon={faTriangleExclamation} />
        </div>
        <h5 className="mfca-card-title">
          {t("Rejection Summary")}
          <Tooltip
            title={t("Total rejection rate (rejected lbs as a share of delivered lbs) plus a breakdown of what happened to the rejected material — disposed locally vs returned or credited.")}
            arrow
            placement="top"
          >
            <span className="mfca-infotip" role="img" aria-label="info">
              <FontAwesomeIcon icon={faCircleInfo} />
            </span>
          </Tooltip>
        </h5>
      </div>

      <div className="rl-rejection-summary-body">
        <div className="rl-summary-block rl-summary-block--inline">
          <span className="rl-summary-block-label">{t("Total rejection rate")}</span>
          <span className={`rl-summary-block-value ${rejectionPctClass(rejectionRate)}`}>
            {fmtPct(rejectionRate)}
          </span>
          <span className="rl-summary-block-context">
            {fmtLbs(totalRejected)} {t("lbs of")} {fmtLbs(totalDelivered)} {t("lbs delivered")}
          </span>
          {rejectionRateDelta != null && rejectionRateDelta !== 0 && (
            <span
              className={`rl-summary-block-delta ${rejectionRateDelta < 0 ? "is-good" : "is-bad"}`}
            >
              {rejectionRateDelta < 0 ? "↓" : "↑"} {Math.abs(rejectionRateDelta).toFixed(1)}pp{" "}
              {t("vs. previous")}
            </span>
          )}
        </div>

        <ul className="rl-disposition-list">
          <li className={`rl-disposition-row ${disposalLoss > 0 ? "" : "is-zero"}`}>
            <span className="rl-disposition-label">{t("Disposal loss")}</span>
            <span className="rl-disposition-value">{fmtMoney(disposalLoss)}</span>
          </li>
          <li className={`rl-disposition-row ${qualityIssue > 0 ? "" : "is-zero"}`}>
            <span className="rl-disposition-label">{t("Quality credit")}</span>
            <span className="rl-disposition-value">{fmtMoney(qualityIssue)}</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

RejectionSummaryCard.propTypes = {
  counters: PropTypes.shape({
    totalDeliveredLbs: PropTypes.number,
    totalRejectedLbs: PropTypes.number,
    rejectionRate: PropTypes.number,
    notUtilized: PropTypes.shape({
      disposalLoss: PropTypes.number,
      qualityIssue: PropTypes.number,
    }),
  }),
  rejectionRateDelta: PropTypes.number,
};

export default RejectionSummaryCard;
